import {
    Component,
    ComponentInterface,
    h,
    Host,
    Prop,
    Element,
    Watch,
    Event,
    EventEmitter,
    State,
    forceUpdate,
} from "@stencil/core";
import { debounce } from "../../utils/debounce";
import {
    ItemsList,
    Option,
    CompareWithFn,
    SearchFn,
    GroupValueFn,
} from "../../utils/items-list";
import { DefaultSelectionModel } from "../../utils/items-list/selection-model";
import { isObject, resolveNested } from "../../utils/utils";

@Component({
    tag: "uic-filter",
    styleUrl: "filter.scss",
})
export class Filter implements ComponentInterface {
    @Element() host: HTMLUicFilterElement;

    private popover: HTMLUicPopoverElement;
    private virtualScroll: HTMLUicVirtualScrollElement;

    private itemsList: ItemsList;
    private lastSelectedItemIndex: number;

    private confirmed = [];
    private isConfirming = false;
    private isSearchEnabled = false;

    @State()
    private visibleItems: Option[];

    @State()
    private searchTerm = "";

    @State()
    private isResetDisabled = true;

    @State()
    private open = false;

    hideSelected = false;

    /**
     * If set to `true`, the user can select multiple items.
     */
    @Prop() multiple = false;

    /**
     * If set to `true`, the user cannot interact with the button.
     */
    @Prop() disabled = false;

    /**
     * The text describing the values to choose from.
     */
    @Prop() label: string;

    /**
     * The items to choose from.
     */
    @Prop() items: any[] = [];

    /**
     * The value of the filter.
     */
    @Prop({ mutable: true }) value: any[] = [];

    /**
     * A function used to compare values.
     * Use it to customize results while typing in the input field.
     */
    @Prop() compareWith?: CompareWithFn;

    /**
     * The text to display instead of the selected option's value.
     */
    @Prop() selectedText?: string;

    /**
     * A function used to customize search results.
     */
    @Prop() searchFn?: SearchFn;

    /**
     * The property path used to get the value of an item.
     */
    @Prop() bindValue: string;

    /**
     * The property path used to get the label of an item.
     */
    @Prop() bindLabel: string = "label";

    /**
     * The property path or function used to group items.
     */
    @Prop() groupBy?: string | Function;

    /**
     * The function used to get the value of a group.
     */
    @Prop() groupValueFn?: GroupValueFn;

    /**
     * If set to `false`, selecting a group will select all of its children but not the group itself.
     */
    @Prop() selectableGroupAsModel = true;

    /**
     * Allow to select group when groupBy is used.
     */
    @Prop() selectableGroup = true;

    /**
     * Allow to search for a value.
     */
    @Prop() searchable = true;

    /**
     * The placeholder text to display for the search field
     */
    @Prop() searchPlaceholderText: string = "Search...";

    /**
     * The text to display for resetting the selection.
     */
    @Prop() resetSelectionText: string = "Reset";

    /**
     * The text to display for confirming the selection.
     */
    @Prop() confirmSelectionText: string = "Confirm";

    /**
     * The text to display for all selected item.
     */
    @Prop() allSelectedText: string = "All";

    /**
     * The text to display for multiple selected items.
     */
    @Prop() multipleSelectedText: string = "Multiple";

    /**
     * The text to display the search term results in empty results.
     */
    @Prop() noSearchResultsText: string = "No search results";

    /**
     * Controls whether popover is hoisted or not. A hoisted popover isn't cut
     * off by its container.
     */
    @Prop() hoist: boolean;

    /**
     * Emitted when the value changes.
     */
    @Event() uicChange: EventEmitter;

    /**
     * Emitted when the search term changes.
     */
    @Event() uicSearch: EventEmitter;

    private readonly debouncedSearch = debounce((e) => this.search(e), 200);

    @Watch("value")
    handleValueChange(value) {
        const emitChange = this.isConfirming;

        if (this.isConfirming) {
            this.isConfirming = false;
        } else {
            // value prop was set from outside the component
            // so validate & sync everything
            this.validateValueProp(value);
            this.itemsList.clearSelected();
            this.syncValueWithItemsList(value);
        }
        this.updateList();

        if (emitChange) {
            this.uicChange.emit(value);
        }
    }

    @Watch("items")
    handleItemsChange(value) {
        this.validateItemsProp(value);

        this.itemsList.setItems(value);
        if (value.length > 0 && this.itemsList.selectedItems.length > 0) {
            this.itemsList.mapSelectedItems();
        }

        this.updateList();
    }

    connectedCallback() {
        this.itemsList = new ItemsList(
            this.host,
            new DefaultSelectionModel() as any
        );

        this.validateValueProp(this.value);
        this.validateItemsProp(this.items);

        this.itemsList.setItems(this.items);
        this.syncValueWithItemsList(this.value);

        this.updateList();
    }

    componentWillRender() {
        this.checkReset();
    }

    private toggleItem(item: Option) {
        if (!item || item.disabled || this.disabled) {
            return;
        }

        if (this.multiple && item.selected) {
            this.unselect(item);
        } else {
            this.select(item);
        }

        this.checkReset();
        this.forceUpdateScroller();
    }

    private select(item: Option) {
        if (!item.selected) {
            this.itemsList.select(item);
        }
    }

    private unselect(item: Option) {
        if (!item) {
            return;
        }

        this.itemsList.unselect(item);
    }

    private updateList() {
        this.confirmed = [...this.itemsList.selectedItems];
        this.visibleItems = this.getVisibleItems();
    }

    private handleReset() {
        this.itemsList.clearSelected();
        this.visibleItems = this.itemsList.items;
        this.lastSelectedItemIndex = -1;

        this.checkReset();
        this.forceUpdateScroller();
    }

    private handleConfirm() {
        this.isConfirming = true;
        this.popover.open = false;
    }

    private handleSearch(event: CustomEvent<string>) {
        event.stopPropagation();
        this.debouncedSearch(event.detail);
    }

    private search(term: string) {
        if (this.isSearchEnabled) {
            this.itemsList.filter(term);
            this.uicSearch.emit(term);
        }
        this.searchTerm = term;
    }

    private handleShow() {
        this.isSearchEnabled = true;
        this.open = true;

        // check the viewport size once layout is done
        requestAnimationFrame(() => this.virtualScroll?.checkViewportSize());
    }

    private handleDismiss() {
        this.isSearchEnabled = false;

        const emitSearch = this.searchTerm !== "";
        this.searchTerm = "";

        if (emitSearch) {
            this.uicSearch.emit("");
        }

        if (this.isConfirming) {
            this.value = this.composeValue();
        } else {
            this.itemsList.clearSelected();
            for (const item of this.confirmed) {
                this.itemsList.select(item);
            }
            this.visibleItems = this.getVisibleItems();
            this.checkReset();
            this.forceUpdateScroller();
        }

        this.open = false;
    }

    private composeValue() {
        const model = [];
        for (const item of this.itemsList.selectedItems) {
            if (this.bindValue) {
                let value = null;
                if (item.children) {
                    value = this.groupValueFn
                        ? item.value
                        : item.value[this.groupBy as string];
                } else {
                    value = resolveNested(item.value, this.bindValue);
                }
                model.push(value);
            } else {
                model.push(item.value);
            }
        }
        return model;
    }

    private forceUpdateScroller() {
        // only use forceUpdate when reference based change detection
        // cannot work, e.g. mutating a property of an item inside bound array
        forceUpdate(this.virtualScroll);
    }

    /**
     * Gets the current value in human readable form.
     *
     * Possible values are:
     *  - custom text provided by selectedText prop
     *  - no or all items: allSelectedText prop
     *  - single group (or item): label of the group (or item)
     *  - single item inside group: label of the group and label of the item
     *  - multiple items: multipleSelectedText props and selected items count
     */
    private getValueText() {
        if (this.selectedText != null) {
            return this.selectedText;
        }

        const items = this.confirmed;
        if (items.length === 0) {
            return this.allSelectedText;
        } else if (items.length === 1) {
            const item = items[0];
            return item.parent
                ? `${item.parent.label} > ${item.label}`
                : item.label;
        } else if (this.itemsList.isSelectAll) {
            return this.allSelectedText;
        } else {
            return `${this.multipleSelectedText} (${this.itemsList.totalSelectedItemCount})`;
        }
    }

    private validateValueProp(prop) {
        if (this.multiple && !Array.isArray(prop)) {
            throw new Error(
                "value: must be an array for selection mode 'multiple'"
            );
        }
    }

    private validateItemsProp(prop) {
        if (!Array.isArray(prop)) {
            throw new Error("items: must be an array");
        }
    }

    private syncValueWithItemsList(value: any) {
        if (this.multiple) {
            value.forEach((v) => this.selectItem(v));
        } else {
            this.selectItem(value);
        }
    }

    private selectItem(value) {
        let item = this.itemsList.findItem(value);
        if (item) {
            this.itemsList.select(item);
        } else {
            const isValObject = isObject(value);
            const isPrimitive = !isValObject && !this.bindValue;
            if (isValObject || isPrimitive) {
                this.itemsList.select(this.itemsList.mapItem(value, null));
            } else if (this.bindValue) {
                item = {
                    [this.bindLabel]: null,
                    [this.bindValue]: value,
                };
                this.itemsList.select(this.itemsList.mapItem(item, null));
            }
        }
    }

    /**
     * This method assembles the list that is shown to the user.
     * We make sure that
     *  1) all previously selected items are placed before all other items
     *  2) group items (an items parent if grouping is enabled) are also visible for selected items
     */
    private getVisibleItems() {
        const items = [];
        let lastParent;
        for (const item of this.confirmed) {
            // optimization: we only perform grouping tests once by remembering the parent
            if (item.parent && item.parent !== lastParent) {
                lastParent = item.parent;

                // edge case: the user is able to select all items of a group individually
                // but not the group itself, so we perform an extra check
                if (lastParent.mixed || lastParent.selected) {
                    items.push(lastParent);
                }
            }

            if (item.children) {
                lastParent = item;
            }

            items.push(item);

            if (this.selectableGroupAsModel && item.children) {
                items.push(...item.children);
            }
        }

        this.lastSelectedItemIndex =
            items.length === 0 ? -1 : items[items.length - 1].index;

        if (this.lastSelectedItemIndex === -1) {
            return this.itemsList.items;
        } else {
            items.push(
                ...this.itemsList.items.filter((item) => !item.selected)
            );
        }

        return items;
    }

    private checkReset() {
        this.isResetDisabled = this.itemsList.selectedItems.length === 0;
    }

    private renderItem = (item: Option) => {
        return (
            <uic-check-list-item
                onUicSelect={() => this.toggleItem(item)}
                class={{
                    "uic-filter__item": true,
                    "uic-filter__item--nested": !!item.parent,
                    "uic-filter__item--separated":
                        !this.searchTerm &&
                        item.index === this.lastSelectedItemIndex,
                }}
                selected={item.selected}
                mixed={item.mixed}
                icon={item.icon}
            >
                {item.label}
            </uic-check-list-item>
        );
    };

    render() {
        const value = this.getValueText();
        const noFilterResults =
            this.searchTerm && this.itemsList.filteredItems.length === 0;
        const data = this.searchTerm
            ? this.itemsList.filteredItems
            : this.visibleItems;

        return (
            <Host
                class={{
                    "uic-filter": true,
                    "uic-filter--open": this.open,
                    "uic-filter--disabled": this.disabled,
                    "uic-filter--no-results": noFilterResults,
                }}
            >
                <uic-popover
                    ref={(el) => (this.popover = el)}
                    onUicShow={() => this.handleShow()}
                    onUicHide={() => this.handleDismiss()}
                    containingElement={this.host}
                    hoist={this.hoist}
                >
                    <div slot="trigger">
                        <slot name="custom-trigger">
                            <button
                                class="uic-filter__button"
                                disabled={this.disabled}
                            >
                                <div class="uic-filter__content">
                                    <div class="uic-filter__label">
                                        <slot name="label">{this.label}</slot>
                                    </div>
                                    <div class="uic-filter__value">{value}</div>
                                </div>
                                <span class="uic-filter__icon">
                                    <uic-icon name="uic:chevron-down"></uic-icon>
                                </span>
                            </button>
                        </slot>
                    </div>
                    <div class="uic-filter__wrapper">
                        {this.searchable && (
                            <uic-search-field
                                onUicChange={(e) => this.handleSearch(e)}
                                placeholder={this.searchPlaceholderText}
                                value={this.searchTerm}
                                class="uic-filter__search"
                            />
                        )}
                        <div class="uic-filter__list">
                            <uic-virtual-scroll
                                class="uic-filter__scroller"
                                ref={(el) => (this.virtualScroll = el)}
                                itemHeight={64}
                                items={data}
                                renderItemFn={this.renderItem}
                            ></uic-virtual-scroll>
                            <div class="uic-filter__no-search-results">
                                <slot name="no-search-result">
                                    <div class="uic-filter__no-search-results-default">
                                        <uic-icon
                                            size="small"
                                            name="uic:flag-information"
                                        ></uic-icon>
                                        <span>{this.noSearchResultsText}</span>
                                    </div>
                                </slot>
                            </div>
                        </div>
                        <uic-footer>
                            <uic-button
                                onClick={() => this.handleReset()}
                                disabled={this.isResetDisabled}
                                slot="secondary"
                                variant="footer"
                            >
                                {this.resetSelectionText}
                            </uic-button>
                            <uic-button
                                onClick={() => this.handleConfirm()}
                                variant="footer-primary"
                            >
                                {this.confirmSelectionText}
                            </uic-button>
                        </uic-footer>
                    </div>
                </uic-popover>
            </Host>
        );
    }
}
